/**
 * Owner: Haselton Baker Risk Group, LLC
 * Product: SP3 - SP3-Ui
 * Developer: Mike McGlone <mike@hbrisk.com>
 * Date: 11/21/17
 * Copyright All Rights Reserved
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import { Field } from 'redux-form';
import { compose, withProps } from 'recompose';
import { withScriptjs } from 'react-google-maps';
import config, { GOOGLE_MAPS_API_KEY } from 'support/config';
import googleMapURL from 'support/maps/googleMapURL';
import FormGroup from 'components/forms/FormGroup';
import Panel from 'components/Panel';
import StateSelectMenu from 'components/forms/StateSelectMenu';
import SiteClassSelectMenu from 'components/forms/SiteClassSelectMenu';
import ZoomLevelSelectMenu, { mapTypesByKey } from 'components/forms/ZoomLevelSelectMenu';
import ReportSectionsToInclude from 'components/forms/ReportSectionsToInclude';
import SpinnerRect from 'components/SpinnerRect';
import RenderField from 'components/forms/RenderField';
import { required, maxLength, noInvalidFilenameChars, zip, lat, lng } from 'support/validators';

/**
 * Initialize a new AutoComplete object
 *
 * @param element
 * @param onPlaceChange
 * @returns {google.maps.places.Autocomplete}
 */
// TODO: After prototype phase, this should be placed elsewhere for easy reuse
const initAutocomplete = (element, onPlaceChange) => {
  // create a new google.maps.places.Autocomplete
  /* eslint-disable no-undef */
  const autocomplete = new google.maps.places.Autocomplete(
    /* eslint-enable */
    // set the DOM element that the new Autocomplete should encapsulate
    element,
    // set the type of prediction to be used
    { types: ['geocode'] }
  );
  // Tell the new AutoComplete what do do when its place changes
  autocomplete.addListener('place_changed', onPlaceChange(autocomplete));
  return autocomplete;
};

class SiteInfoForm extends Component {

  constructor() {
    super();
    this.state = {
      disableSubmit: false,
      showOptions: false,
    };
    this.enableSubmit = this.enableSubmit.bind(this);
    this.disableSubmit = this.disableSubmit.bind(this);
    this.onPlaceChange = this.onPlaceChange.bind(this);
    this.toggleOptions = this.toggleOptions.bind(this);
  }

  componentDidMount() {
    // Todo: Ultimately use of document.getElementById should be replaced by React-based approach
    initAutocomplete(
      document.getElementById('autocomplete'), this.onPlaceChange);
  }

  onPlaceChange(autocomplete) {
    return () => {
      if (this.props.updateLocationFields(autocomplete)) {
        this.enableSubmit();
      }
    };
  }

  disableSubmit() {
    this.setState({
      disableSubmit: true,
    });
  }

  enableSubmit() {
    this.setState({
      disableSubmit: false,
    });
  }

  toggleOptions(e) {
    e.preventDefault();
    this.setState(prev => ({
      showOptions: !prev.showOptions,
    }));
  }

  render() {
    const { error, handleSubmit, submitting, shouldRedirect } = this.props;
    if (shouldRedirect) {
      return (
        <Redirect to={{ pathname: '/list' }} />
      );
    }

    const submitButton = submitting
      ? <div className="pull-right"><SpinnerRect /></div>
      : (
        <button
          className="btn btn-primary pull-right"
          disabled={this.state.disableSubmit}
          type="submit"
        >
          Create
        </button>
      )
    ;

    const alert = error && !submitting ? <div className="alert alert-danger">{error}</div> : null;

    return (
      <form className="site-info-form" onSubmit={handleSubmit}>
        <div className="row">
          <div className="col-sm-12">
            {alert}
            <fieldset>
              <legend>Site Name</legend>
              <Field
                className="form-control"
                name="name"
                component={RenderField}
                type="text"
                id="name"
                validate={[required, maxLength(200), noInvalidFilenameChars]}
              />
            </fieldset>
            <fieldset>
              <legend>Site Location</legend>
              <FormGroup
                labelText="Enter your Address"
                labelClasses="sr-only"
                forValue="autocomplete"
              >
                <Field
                  className="form-control"
                  component="input"
                  id="autocomplete"
                  name="autocomplete"
                  type="text"
                  placeholder="Start typing your site address here ..."
                  onFocus={this.disableSubmit}
                  onBlur={this.enableSubmit}
                  onChange={this.disableSubmit}
                />
              </FormGroup>
              <Panel className="row">
                <div className="col-sm-12">
                  <div className="row">
                    <div className="col-sm-12">
                      <Field
                        label="Street"
                        className="form-control"
                        name="street"
                        component={RenderField}
                        type="text"
                        id="street"
                        validate={[required, maxLength(150)]}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-sm-12">
                      <Field
                        label="City"
                        className="form-control"
                        name="city"
                        component={RenderField}
                        type="text"
                        id="city"
                        validate={[required, maxLength(100)]}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-sm-6">
                      <StateSelectMenu validate={[required]} />
                    </div>
                    <div className="col-sm-6">
                      <FormGroup labelText="Zip" forValue="zip">
                        <Field
                          className="form-control"
                          name="zip"
                          component={RenderField}
                          type="text"
                          id="zip"
                          validate={[required, zip]}
                        />
                      </FormGroup>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-sm-6">
                      <FormGroup labelText="Latitude" forValue="lat">
                        <Field
                          className="form-control"
                          name="lat"
                          component={RenderField}
                          type="text"
                          id="lat"
                          validate={[required, lat]}
                        />
                      </FormGroup>
                    </div>
                    <div className="col-sm-6">
                      <FormGroup labelText="Longitude" forValue="lng">
                        <Field
                          className="form-control"
                          name="lng"
                          component={RenderField}
                          type="text"
                          id="lng"
                          validate={[required, lng]}
                        />
                      </FormGroup>
                    </div>
                  </div>
                </div>
              </Panel>
            </fieldset>
          </div>
        </div>
        <div className="row">
          <div className="col-sm-12">
            <button className="options-header btn btn-link" onClick={this.toggleOptions}>
              <h5>Additional Options</h5>
            </button>
            <fieldset
              id="additional-options-fieldset"
              className={this.state.showOptions ? '' : 'hidden'}
            >
              <fieldset>
                <legend>Site Class</legend>
                <div className="col-sm-12">
                  <SiteClassSelectMenu />
                </div>
              </fieldset>
              <fieldset>
                <legend>Zoom Levels</legend>
                <div className="row">
                  <div className="col-sm-6 col-xs-12">
                    <ZoomLevelSelectMenu mapType={mapTypesByKey.soil} />
                  </div>
                  <div className="col-sm-6 col-xs-12">
                    <ZoomLevelSelectMenu mapType={mapTypesByKey.liquefaction} />
                  </div>
                </div>
                <div className="row">
                  <div className="col-sm-6 col-xs-12">
                    <ZoomLevelSelectMenu mapType={mapTypesByKey.landslide} />
                  </div>
                  <div className="col-sm-6 col-xs-12">
                    <ZoomLevelSelectMenu mapType={mapTypesByKey.faultRupture} />
                  </div>
                </div>
                <div className="row">
                  <div className="col-sm-6 col-xs-12">
                    <ZoomLevelSelectMenu mapType={mapTypesByKey.tsunami} />
                  </div>
                  <div className="col-sm-6 col-xs-12">
                    <ZoomLevelSelectMenu mapType={mapTypesByKey.faults} />
                  </div>
                </div>
                <div className="row">
                  <div className="col-sm-6 col-xs-12">
                    <ZoomLevelSelectMenu mapType={mapTypesByKey.historicShaking} />
                  </div>
                  <div className="col-sm-6 col-xs-12">
                    <ZoomLevelSelectMenu mapType={mapTypesByKey.damInundation} />
                  </div>
                </div>
              </fieldset>
              <fieldset>
                <legend>Report Sections to Include</legend>
                <ReportSectionsToInclude />
              </fieldset>
            </fieldset>
            {submitButton}
          </div>
        </div>
      </form>
    );
  }
}

SiteInfoForm.defaultProps = {
  error: '',
};

SiteInfoForm.propTypes = {
  error: PropTypes.string,
  handleSubmit: PropTypes.func.isRequired,
  updateLocationFields: PropTypes.func.isRequired,
  shouldRedirect: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
};

export default compose(
  withProps({
    googleMapURL: googleMapURL(config(GOOGLE_MAPS_API_KEY)),
    loadingElement: <div />,
  }),
  withScriptjs
)(SiteInfoForm);
